import { FC } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@new/common/ui';
import { Loader } from '@new/common/ui/Loader/Loader';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import type { User as UserType } from '@teachur/http-client';

interface InstructorListProps {
  instructors: UserType[];
  isLoading: boolean;
  searchQuery: string;
  mainInstructorId?: number;
  onDelete: (instructor: UserType) => void;
  onSetMainInstructor: (instructor: UserType) => void;
  isDeleting: boolean;
  isSettingMainInstructor: boolean;
}

export const InstructorList: FC<InstructorListProps> = ({
  instructors,
  isLoading,
  searchQuery,
  mainInstructorId,
  onDelete,
  onSetMainInstructor,
  isDeleting,
  isSettingMainInstructor,
}) => {
  if (isLoading) {
    return <Loader showLogo={false} />;
  }

  if (instructors.length === 0) {
    return (
      <div className="p-6 pt-0">
        <EmptyState
          message={
            searchQuery
              ? 'No instructors found matching your search.'
              : 'No instructors assigned to this course yet.'
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {instructors.map((instructor) => {
        const isMain = instructor.id === mainInstructorId;
        return (
          <div
            key={instructor.id}
            className="flex flex-wrap items-center justify-between gap-6 p-4 border-2 border-(--neutral-black-NB100) rounded-lg bg-(--neutral-white-NW1000) shadow-sm"
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-(--primary-O200) text-(--primary-O900) font-bold shrink-0">
                {instructor.firstName?.[0]}
                {instructor.lastName?.[0]}
              </div>

              <div className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center gap-2">
                  <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB1000)! font-semibold m-0!">
                    {instructor.firstName} {instructor.lastName}
                  </p>
                  {isMain && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-(--primary-O200) text-(--primary-O900) text-xs font-semibold">
                      Main
                    </span>
                  )}
                </div>
                <p className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB700)! m-0!">
                  {instructor.email}
                </p>
              </div>
            </div>

            <div className="flex items-end justify-end gap-2 w-full md:w-auto">
              <Button
                variant={isMain ? 'ghost' : 'primary'}
                size="sm"
                onClick={() => onSetMainInstructor(instructor)}
                disabled={isSettingMainInstructor}
              >
                {isMain ? 'Unset Main' : 'Set as Main'}
              </Button>
              <Button
                variant="neutral"
                size="sm"
                onClick={() => onDelete(instructor)}
                disabled={isDeleting}
              >
                <Trash2 size={16} className="text-(--neutral-black-NB600)!" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
